/**
 * `GET`/`PUT /v1/me/table-prefs/:tableKey` — hvilke kolonner DENNE leseren vil se (ordren Del 3).
 *
 * =================================================================================================
 * IKKE ORGANISASJONSNØKLET, OG DET ER IKKE EN FORGLEMMELSE.
 *
 * Nøkkelen henger under `qk.global()` sammen med `me`, fordi valget er lagret per MENNESKE og ikke
 * per hus: en agent som har lært seg å lese registeret med provisjonssatsen først skal ha den
 * først i begge husene sine. Å legge den under `qk.org(orgId)` ville tømt den ved hvert husbytte
 * og hentet den samme raden på nytt.
 *
 * =================================================================================================
 * SKRIVINGEN ER OPTIMISTISK, FORDI ORDREN SIER «ENDRINGER VIRKER MED EN GANG».
 *
 * En avkryssing som venter på et rundtur til serveren før kolonnen forsvinner er en avkryssing man
 * trykker to ganger. Cachen settes derfor først, `PUT` går etterpå, og en feil ruller tilbake til
 * det som faktisk var lagret — DESIGN.md §7, og den samme formen `useUpdateMySettings` har.
 *
 * Det som IKKE er optimistisk er lesingen: fram til den første `GET`-en er svaret katalogens
 * standard, som er nøyaktig det en bruker uten lagret valg skal se. Ingen skeleton, ingen hopp.
 */
import { tablePrefsDtoSchema, type TableColumnPref, type TablePrefsDto } from '@tvitvi/shared';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { apiRequest } from '@/api/client';
import { CACHE } from '@/api/cache-tiers';
import { qk } from '@/api/query-keys';
import {
  isDefaultColumnPrefs,
  resolveColumnPrefs,
  type ColumnCatalogue,
} from '@/components/ui/column-prefs';

export function tablePrefsQueryOptions(tableKey: string) {
  return queryOptions({
    queryKey: qk.tablePrefs(tableKey),
    queryFn: ({ signal }) =>
      apiRequest({
        path: `/me/table-prefs/${tableKey}`,
        schema: tablePrefsDtoSchema,
        signal,
        /* Valget er personens, ikke husets — ruta er `@OrgScope('none')`, som `/me/settings`. */
        sendOrganizationHeader: false,
      }),
    ...CACHE.identity,
  });
}

export interface TablePrefsHandle {
  /** Alltid katalogens nøkler, én gang hver, i den rekkefølgen tabellen skal tegne dem. */
  prefs: TableColumnPref[];
  /** Skriv hele valget. Virker med en gang i cachen, lagres etterpå. */
  setPrefs: (next: readonly TableColumnPref[]) => void;
  /** Tilbake til katalogens standard — sletter raden, så leseren følger med når katalogen endres. */
  reset: () => void;
  isDefault: boolean;
}

/**
 * Kolonnevalget for én tabell, lest mot katalogen og klart til å skrives.
 *
 * Katalogen kommer inn utenfra fordi den er sidens: serveren kjenner den ikke, og de to reglene
 * som gjør at et lagret valg overlever en katalogendring bor i `column-prefs.ts` — der de er
 * enhetstestet.
 */
export function useTablePrefs(tableKey: string, catalogue: ColumnCatalogue): TablePrefsHandle {
  const queryClient = useQueryClient();
  const saved = useQuery(tablePrefsQueryOptions(tableKey));

  const prefs = useMemo(
    () => resolveColumnPrefs(catalogue, saved.data?.columns),
    [catalogue, saved.data],
  );

  const save = useMutation({
    mutationFn: (columns: readonly TableColumnPref[]) =>
      apiRequest({
        path: `/me/table-prefs/${tableKey}`,
        method: 'PUT',
        body: { columns },
        schema: tablePrefsDtoSchema,
        sendOrganizationHeader: false,
      }),
    onMutate: async (columns) => {
      await queryClient.cancelQueries({ queryKey: qk.tablePrefs(tableKey) });
      const previous = queryClient.getQueryData<TablePrefsDto>(qk.tablePrefs(tableKey));
      queryClient.setQueryData<TablePrefsDto>(qk.tablePrefs(tableKey), {
        tableKey,
        columns: [...columns],
      });
      return { previous };
    },
    onError: (_error, _columns, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(qk.tablePrefs(tableKey), context.previous);
      }
    },
    /*
     * Den optimistiske skrivingen må synkronisere ved FEIL også, og `MutationCache`-handleren som
     * eier `meta.invalidates` kjører bare ved suksess. Samme unntak som `useUpdateMySettings`.
     */
    onSettled: () => queryClient.invalidateQueries({ queryKey: qk.tablePrefs(tableKey) }),
  });

  const setPrefs = useCallback(
    (next: readonly TableColumnPref[]) => {
      save.mutate(next);
    },
    [save],
  );

  const reset = useCallback(() => {
    /*
     * EN TOM LISTE, IKKE KATALOGENS STANDARD SKREVET UT.
     *
     * De to ser like ut på skjermen i dag og er ikke det samme i morgen: en lagret kopi av dagens
     * standardsett blir stående når katalogen får en kolonne, mens en slettet rad følger med.
     * Serveren sletter raden på en tom liste nettopp av den grunnen.
     */
    save.mutate([]);
  }, [save]);

  return {
    prefs,
    setPrefs,
    reset,
    isDefault: isDefaultColumnPrefs(catalogue, prefs),
  };
}
